import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/header';
import ProtectedRoute from './components/ProtectedRoute';
import { RoleProvider } from './context/RoleContext';
import AdminRoute from './components/Admin_components/AdminRoute';
import { initializeUTMTracking } from './utils/utm';
import { trackPageView } from './utils/gtm';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { LocationProvider } from './contexts/LocationContext';
import './App.css';

const Public = lazy(() => import('./components/public'));
const Booking = lazy(() => import('./components/booking'));
const Confirmation = lazy(() => import('./components/confirmation'));
const Teacher = lazy(() => import('./components/teacher'));
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./components/RefundPolicy'));
const Onboarding = lazy(() => import('./components/onboarding'));
const StudentOnboarding = lazy(() => import('./components/StudentOnboarding'));
const VerificationPending = lazy(() => import('./components/verificationPending'));
const AdminDashboard = lazy(() => import('./components/Admin_components/AdminDashboard'));
const AdminLogin = lazy(() => import('./components/Admin_components/AdminLogin'));
const BlogEdit = lazy(() => import('./components/Admin_components/BlogEdit'));
const BlogNew = lazy(() => import('./components/Admin_components/BlogNew'));
const BlogHomepage = lazy(() => import('./components/Blog/BlogHomepage'));
const BlogArticle = lazy(() => import('./components/Blog/BlogArticle'));
const TeacherLandingPage = lazy(() => import('./components/TeacherLandingPage'));
const TeacherSearch = lazy(() => import('./components/TeacherSearch'));
const WhyDanceHall = lazy(() => import('./components/WhyDanceHall'));
const NotFound = lazy(() => import('./components/NotFound'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const RequestGenre = lazy(() => import('./components/RequestGenre'));
const PerformanceOpportunities = lazy(() => import('./components/PerformanceOpportunities'));



function App() {
  const location = useLocation();

  useEffect(() => {
    // Initialize UTM tracking
    initializeUTMTracking();
    
    // Test GTM - push a custom event
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'app_initialized',
        page_title: document.title,
        page_location: window.location.href
      });
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);

  return (
    <RoleProvider>
      <LocationProvider>
        <CurrencyProvider>
          <div className="App">
            <Header />
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>Loading...</div>}>
          <Routes>
          <Route path="/" element={<Public />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/booking" element={<ProtectedRoute component={Booking} allowedRoles={['student']} />} />
          <Route path="/confirmation" element={<ProtectedRoute component={Confirmation} allowedRoles={['student']} />} />
          <Route path="/teacher" element={<ProtectedRoute component={Teacher} allowedRoles={['teacher']} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/why-dancehall" element={<WhyDanceHall />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/request-genre" element={<RequestGenre />} />
          <Route path="/newsletter" element={<PerformanceOpportunities />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/onboarding/student" element={<StudentOnboarding />} />
          <Route path="/onboarding/teacher" element={<Onboarding />} />
          <Route path="/verification-pending" element={<VerificationPending />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminRoute component={AdminDashboard} />} />
          <Route path="/admin/blog/new" element={<AdminRoute component={BlogNew} />} />
          <Route path="/admin/blog/edit/:id" element={<AdminRoute component={BlogEdit} />} />
          <Route path="/blog" element={<BlogHomepage />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/join/:teacherSlug" element={<TeacherLandingPage />} />
          <Route path="/teachers" element={<TeacherSearch />} />
          <Route path="/teachers/:danceStyle" element={<TeacherSearch />} />
          
          {/* Redirect common route mistakes */}
          <Route path="/teacher/search" element={<Navigate to="/teachers" replace />} />
          <Route path="/browse" element={<Navigate to="/teachers" replace />} />
          <Route path="/browse/teachers" element={<Navigate to="/teachers" replace />} />
          <Route path="/search" element={<Navigate to="/teachers" replace />} />
          <Route path="/search/teachers" element={<Navigate to="/teachers" replace />} />
          <Route path="/find-teachers" element={<Navigate to="/teachers" replace />} />
          
          {/* 404 - Catch all unmatched routes */}
          <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </div>
        </CurrencyProvider>
      </LocationProvider>
    </RoleProvider>
  );
}

export default App;
